import type {
  MovimentarPendenciasPayload,
  MovimentarPendenciasResultado,
} from '@/types/processoArbitral/pendenciasMovimentacao';

export const MOTIVO_NAO_MOVIMENTADA_LABEL: Record<string, string> = {
  CONFLITO: 'já foi movimentada por outra pessoa',
  TIPO_BLOQUEADO: 'este tipo não se resolve pela aba',
  STATUS_BLOQUEADO: 'o status atual não permite esta ação',
  NAO_ENCONTRADA: 'não encontrada neste processo',
};

/**
 * Monta o body de `POST /admin/arbitral/pendencias/movimentacoes`. Campo falsy é omitido em vez
 * de ir como `null`: o core valida presença, não valor.
 */
export function toMovimentarPendenciasBody(
  payload: MovimentarPendenciasPayload,
): Record<string, unknown> {
  return {
    clientRequestId: payload.clientRequestId,
    processoArbitralId: payload.processoArbitralId,
    pendenciaIds: payload.pendenciaIds,
    desfecho: payload.desfecho,
    anexoUploadIds: payload.anexoUploadIds,
    ...(payload.resposta ? { resposta: payload.resposta } : {}),
    ...(payload.prazoDiasUteis ? { prazoDiasUteis: payload.prazoDiasUteis } : {}),
    ...(payload.dataVencimento ? { dataVencimento: payload.dataVencimento } : {}),
  };
}

/**
 * O 409 do backend (nenhuma pendência movimentada) chega como texto, com o id cru e o código
 * do motivo — `"... da360092-… (STATUS_BLOQUEADO)"`. Ilegível para quem opera. Troca os
 * códigos pelos mesmos rótulos do resultado parcial e tira os UUIDs, que não dizem nada a
 * ninguém: a lista já foi relida e mostra o estado verdadeiro.
 */
export function humanizarRecusa(mensagem: string): string {
  const semIds = mensagem.replace(
    /\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b[,\s]*/gi,
    '',
  );
  const traduzida = Object.entries(MOTIVO_NAO_MOVIMENTADA_LABEL)
    .sort(([a], [b]) => b.length - a.length)
    .reduce((texto, [codigo, rotulo]) => texto.replaceAll(codigo, rotulo), semIds);
  return traduzida
    .replace(/\s{2,}/g, ' ')
    .replace(/:\s*\(/, ': (')
    .trim();
}

/** Resumo de um 200 parcial: quantas foram e por que as demais ficaram de fora. */
export function resumoDeConflitos(resultado: MovimentarPendenciasResultado): string {
  const total = resultado.movimentadas.length + resultado.naoMovimentadas.length;
  const motivos = [
    ...new Set(
      resultado.naoMovimentadas.map(
        (item) => MOTIVO_NAO_MOVIMENTADA_LABEL[item.motivo] ?? item.motivo,
      ),
    ),
  ];
  return `${resultado.movimentadas.length} de ${total} movimentadas. As demais: ${motivos.join('; ')}.`;
}
